import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../../Context/ThemeContext';
import DataTable, { Column } from '../../../Forms/DataGrid/DataTable/DataTable';
import { AdministratorHandler } from '../../Administrators/AdministratorHandler';
import { GetAllAdminsByFilterCommandRequest, SlimAdministrator } from '../../../../Core/Api/Api';

interface AdministratorListViewProps {
    onAddAdministrator?: () => void;
    onViewAdministrator?: (admin: SlimAdministrator) => void;
}

const AdministratorListView: React.FC<AdministratorListViewProps> = ({
    onAddAdministrator,
    onViewAdministrator,
}) => {
    const navigate = useNavigate();
    const { theme, isMobile } = useTheme();
    const [administrators, setAdministrators] = useState<SlimAdministrator[]>([]);
    const [loading, setLoading] = useState<boolean>(true);

    const handler = new AdministratorHandler();

    useEffect(() => {
        const fetchAllAdministrators = async () => {
            setLoading(true);
            try {
                const request = new GetAllAdminsByFilterCommandRequest();
                request.pageNumber = 1;
                request.amountPerPage = 1000; // Fetch all administrators

                const response = await handler.GetData(request);

                // Sort alphabetically by username by default
                const sortedAdmins = (response.results || []).sort((a, b) =>
                    (a.username || '').localeCompare(b.username || '')
                );

                setAdministrators(sortedAdmins);
            } catch (error) {
                console.error('Error fetching administrator data:', error);
                setAdministrators([]);
            } finally {
                setLoading(false);
            }
        };

        fetchAllAdministrators();
    }, []);

    const getRoleColor = (roles: string[] | undefined) => {
        if (!roles || roles.length === 0) return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };

        const role = roles[0]; // Use first role for color
        if (role.toLowerCase().includes('super') || role.toLowerCase().includes('senior')) {
            return { bg: 'rgba(168, 85, 247, 0.2)', color: '#a855f7' };
        }
        if (role.toLowerCase().includes('admin')) {
            return { bg: 'rgba(59, 130, 246, 0.2)', color: '#3b82f6' };
        }
        return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Active': return { bg: 'rgba(34, 197, 94, 0.2)', color: '#22c55e' };
            case 'Disabled': return { bg: 'rgba(239, 68, 68, 0.2)', color: '#ef4444' };
            case 'Locked': return { bg: 'rgba(251, 146, 60, 0.2)', color: '#fb923c' };
            default: return { bg: 'rgba(107, 114, 128, 0.2)', color: '#6b7280' };
        }
    };

    const columns: Column<SlimAdministrator>[] = [
        {
            title: 'Username',
            dataIndex: 'username',
            sorter: true,
            render: (value) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#8b5cf6', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '14px', fontWeight: 600 }}>
                        {String(value).charAt(0).toUpperCase()}
                    </div>
                    <span style={{ fontWeight: 500 }}>{String(value)}</span>
                </div>
            ),
        },
        { title: 'Email', dataIndex: 'email', sorter: true },
        { title: 'First Name', dataIndex: 'firstName', sorter: true },
        { title: 'Last Name', dataIndex: 'lastName', sorter: true },
        {
            title: 'Role',
            dataIndex: 'roles',
            sorter: true,
            render: (value, record) => {
                const roles = record.roles || [];
                const colors = getRoleColor(roles);
                return (
                    <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: colors.bg,
                        color: colors.color,
                    }}>
                        {roles.length > 0 ? roles.join(', ') : 'No Role'}
                    </span>
                );
            },
        },
        {
            title: 'Status',
            dataIndex: 'status',
            sorter: true,
            render: (value) => {
                const colors = getStatusColor(String(value));
                return (
                    <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        background: colors.bg,
                        color: colors.color,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px'
                    }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: colors.color }} />
                        {String(value)}
                    </span>
                );
            },
        },
        {
            title: 'Actions',
            dataIndex: 'userId',
            render: (_, record) => (
                <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                        onClick={(e) => { e.stopPropagation(); onViewAdministrator?.(record); }}
                        style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600, background: '#2563eb', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        View
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); console.log('Edit:', record.userId); }}
                        style={{ padding: '6px 12px', fontSize: '12px', fontWeight: 600, background: 'transparent', color: theme.text, border: `1px solid ${theme.inputBorder}`, borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Edit
                    </button>
                </div>
            ),
        },
    ];

    const styles: Record<string, React.CSSProperties> = {
        container: { padding: 0 },
        header: { marginBottom: '24px' },
        title: { fontSize: isMobile ? '24px' : '28px', fontWeight: 700, color: theme.titleColor, marginBottom: '8px' },
        subtitle: { fontSize: '14px', color: theme.textSecondary, marginBottom: '16px' },
        headerActions: { display: 'flex', gap: '12px', flexWrap: 'wrap' as const },
        addButton: {
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: 600,
            background: '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background 0.2s'
        },
        statsRow: { display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' },
        statCard: { background: theme.card, borderRadius: '8px', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
        statLabel: { fontSize: '13px', color: theme.textSecondary, marginBottom: '4px' },
        statValue: { fontSize: '24px', fontWeight: 700, color: theme.text },
    };

    const activeAdmins = administrators.filter(a => a.status === 'Active').length;
    const superAdmins = administrators.filter(a =>
        a.roles?.some(role => role.toLowerCase().includes('super') || role.toLowerCase().includes('senior'))
    ).length;

    const stats = [
        { label: 'Total Administrators', value: administrators.length },
        { label: 'Active', value: activeAdmins },
        { label: 'Super Admins', value: superAdmins },
        { label: 'With Roles', value: administrators.filter(a => a.roles && a.roles.length > 0).length },
    ];

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Administrators</h1>
                <p style={styles.subtitle}>Manage system administrators and their permissions</p>
                <div style={styles.headerActions}>
                    <button
                        style={styles.addButton}
                        onClick={onAddAdministrator ?? (() => navigate('Add'))}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#16a34a'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#22c55e'; }}
                    >
                        <span>+</span> Add Administrator
                    </button>
                </div>
            </div>

            <div style={styles.statsRow}>
                {stats.map((stat) => (
                    <div key={stat.label} style={styles.statCard}>
                        <div style={styles.statLabel}>{stat.label}</div>
                        <div style={styles.statValue}>{stat.value}</div>
                    </div>
                ))}
            </div>

            <DataTable<SlimAdministrator>
                columns={columns}
                dataSource={administrators}
                rowKey="userId"
                loading={loading}
                pageSize={10}
                onRowClick={onViewAdministrator}
            />
        </div>
    );
};

export default AdministratorListView;